import "./notificationAlert.scss";

import {
    Box,
    Divider,
    HStack,
    Image,
    Pressable,
    Toast,
    VStack
} from "@hosttools/frontend/components";
import type React from "react";
import {memo, useCallback} from "react";
import type {NativeSyntheticEvent, NativeTouchEvent} from "react-native";

import Text from "@/client/components/Text";

interface Props {
    toastId: string;
    title: string;
    subTitle: string;
    message: string;
    logoKey: string;
    logoUrl: string;
    onToastClick?: () => void;
    onCloseToast?: (toastId: string) => void;
}

const NotificationAlert: React.FC<Props> = ({
    toastId,
    title,
    subTitle,
    message,
    logoKey,
    logoUrl,
    onToastClick,
    onCloseToast
}) => {
    const handleToastClick = useCallback(() => {
        onToastClick?.();
        onCloseToast?.(toastId);
    }, [onToastClick, onCloseToast, toastId]);

    const handleCloseToast = useCallback(
        (e: NativeSyntheticEvent<NativeTouchEvent>) => {
            e.stopPropagation();
            onCloseToast?.(toastId);
        },
        [onCloseToast, toastId]
    );

    return (
        <Box className="px-4" testID="message-notification-alert">
            <Toast action="info" id={toastId}>
                <Pressable className="flex items-center w-full" onPress={handleToastClick}>
                    <Box className="bg-white rounded-lg shadow-subtleShadow px-4 w-96">
                        <HStack className="items-center h-full" space="lg">
                            <HStack className="items-center flex-1" space="lg">
                                {!!logoUrl && (
                                    <Image
                                        alt={logoKey}
                                        className="rounded-full w-10 h-10"
                                        source={{uri: logoUrl}}
                                    />
                                )}
                                <VStack className="flex-1" space="xs">
                                    <Text isTruncated color="blueGray.800" variant="sm">
                                        {title}
                                    </Text>
                                    <Text isTruncated color="blueGray.400" variant="xs">
                                        {subTitle}
                                    </Text>
                                    <Text numberOfLines={2} variant="sm">
                                        {message}
                                    </Text>
                                </VStack>
                            </HStack>
                            <Divider className="h-auto self-stretch" orientation="vertical" />
                            <Pressable
                                className="items-center justify-center px-3"
                                onPress={handleCloseToast}
                            >
                                <Text color="blue.600" variant="sm">
                                    Close
                                </Text>
                            </Pressable>
                        </HStack>
                    </Box>
                </Pressable>
            </Toast>
        </Box>
    );
};

export default memo(NotificationAlert);
